import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { RegionSuggestion, WorldSummary } from '@jetopolis/shared';
import { api, ApiError } from '../../app/api/client';
import { useAuth } from '../../app/stores/auth';
import { useOverlays } from '../../app/stores/overlays';
import { Button } from '../../ui/Button';
import { Chip } from '../../ui/Chip';
import { EmptyState } from '../../ui/EmptyState';
import { Panel } from '../../ui/Panel';
import { Skeleton } from '../../ui/Skeleton';
import { Stat } from '../../ui/Stat';
import { LocalePicker } from '../settings/LocalePicker';

function WorldCard({ world }: { world: WorldSummary }) {
  const { t } = useTranslation();
  const year = Number(world.gameDate.slice(0, 4));
  const yearsLeft = Math.max(0, world.endYear - year);

  return (
    <Panel className="p-4">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <h3 className="text-[14px] text-txt">{world.name}</h3>
          <div className="mt-1 flex flex-wrap gap-1">
            <Chip tone={world.type === 'BLITZ' ? 'amber' : 'cyan'}>
              {t(`lobby.type.${world.type}`)}
            </Chip>
            <Chip>{t(`lobby.region.${world.region}`)}</Chip>
            {world.status !== 'RUNNING' && <Chip tone="red">{t(`hq.status.${world.status}`)}</Chip>}
          </div>
        </div>
        <Link to={`/w/${world.id}`}>
          <Button variant={world.isMember ? 'primary' : 'ghost'}>
            {world.isMember ? t('lobby.card.continue') : t('lobby.card.join')}
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Stat label={t('hq.world.date')} value={world.gameDate} tone="amber" />
        <Stat label={t('hq.world.players')} value={`${world.players}/${world.maxPlayers}`} />
        <Stat
          label={t('hq.world.years')}
          value={t('lobby.card.toFinale', { years: yearsLeft })}
          tone="dim"
        />
      </div>
    </Panel>
  );
}

export function Lobby() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const openAuth = useOverlays((s) => s.open);

  const [worlds, setWorlds] = useState<WorldSummary[] | null>(null);
  const [region, setRegion] = useState<RegionSuggestion | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const [list, suggestion] = await Promise.all([
          api.get<WorldSummary[]>('/v1/worlds'),
          api.get<RegionSuggestion>('/v1/region'),
        ]);
        if (!alive) return;
        setWorlds(list);
        setRegion(suggestion);
      } catch (e) {
        if (!alive) return;
        setErrorKey(e instanceof ApiError ? e.code : 'errors.common.internal');
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const mine = worlds?.filter((w) => w.isMember) ?? [];
  const others = worlds?.filter((w) => !w.isMember) ?? [];

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-[880px] flex-col gap-4 px-4 pb-10 pt-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="font-[family-name:var(--font-display)] text-[20px] text-txt">
          {t('lobby.title')}
        </h1>
        <div className="flex items-center gap-2">
          <LocalePicker compact />
          {user?.isAnonymous === true && (
            <Button
              onClick={() => {
                openAuth('auth');
              }}
            >
              {t('auth.guest.linkCta')}
            </Button>
          )}
        </div>
      </div>

      {region && (
        <p className="text-[12px] text-mut">
          {t('lobby.region.yours', { region: t(`lobby.region.${region.suggested}`) })}
        </p>
      )}

      {errorKey !== null && <p className="text-[13px] text-red">{t(errorKey)}</p>}

      {worlds === null && errorKey === null && (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-[132px]" />
          <Skeleton className="h-[132px]" />
        </div>
      )}

      {mine.length > 0 && (
        <section className="flex flex-col gap-3">
          <h2 className="text-[10px] uppercase tracking-wider text-dim">
            {t('lobby.section.mine')}
          </h2>
          {mine.map((w) => (
            <WorldCard key={w.id} world={w} />
          ))}
        </section>
      )}

      {others.length > 0 && (
        <section className="flex flex-col gap-3">
          <h2 className="text-[10px] uppercase tracking-wider text-dim">
            {t('lobby.section.all')}
          </h2>
          {others.map((w) => (
            <WorldCard key={w.id} world={w} />
          ))}
        </section>
      )}

      {worlds !== null && worlds.length === 0 && (
        <EmptyState title={t('lobby.empty.title')} hint={t('lobby.empty.hint')} />
      )}
    </main>
  );
}
